/**
 * A grouped view of peer states.
 */
export class PeerStateView {
    value;
    constructor(value) {
        this.value = value;
    }
    /**
     * Get the presence state of all peers. By default, each peer is its own
     * group, but presence activity can be aggregated by arbitrary criteria.
     *
     * @param opts
     * @returns presence state for all groups
     */
    getStates(opts) {
        const groupingFn = opts?.groupingFn ?? peerIdentity;
        const summaryFn = opts?.summaryFn ??
            getLastActivePeer;
        const statesByKey = Object.values(this.value).reduce((byKey, curr) => {
            const key = groupingFn(curr);
            if (!(key in byKey)) {
                byKey[key] = [];
            }
            byKey[key].push(curr);
            return byKey;
        }, {});
        return Object.entries(statesByKey).reduce((result, [key, states]) => {
            result[key] = summaryFn(states);
            return result;
        }, {});
    }
}
/**
 * Get the peerId of this peer.
 *
 * @param peer
 * @returns peer id
 */
export function peerIdentity(peer) {
    return peer.peerId;
}
/**
 * Find the peer that most recently sent a state update.
 *
 * @param peers
 * @returns id of most recently active peer
 */
export function getLastActivePeer(peers) {
    let freshestLastActiveAt;
    return peers.reduce((freshest, curr) => {
        const lastActiveAt = curr.lastActiveAt;
        if (!lastActiveAt) {
            return freshest;
        }
        if (!freshest || lastActiveAt > freshestLastActiveAt) {
            freshestLastActiveAt = lastActiveAt;
            return curr;
        }
        return freshest;
    }, undefined);
}
/**
 * Find the peer that most recently sent a heartbeat.
 *
 * @param peers
 * @returns id of most recently seen peer
 */
export function getLastSeenPeer(peers) {
    let freshestLastSeenAt;
    return peers.reduce((freshest, curr) => {
        const lastSeenAt = curr.lastSeenAt;
        if (!lastSeenAt) {
            return freshest;
        }
        if (!freshest || lastSeenAt > freshestLastSeenAt) {
            freshestLastSeenAt = lastSeenAt;
            return curr;
        }
        return freshest;
    }, undefined);
}
